import { Flex } from "antd";
import { InfiniteScrollList } from "../controls/InfiniteScrollList";
import { BrandKitAsset, MediaAsset } from "pages/media/types";
import { MediaAssetPreview } from "./MediaAssetPreview";
import { getRowKey } from "../../../../helpers";
import { MediaAssetTabs, MediaKind } from "../../../../types";
import {
  AssetControlsOverlay,
  GalleryAsset,
} from "../controls/AssetControlsOverlay";
import { useState, useRef, useMemo, useCallback } from "react";
import { DeleteButton } from "components/DeleteButton/";
import { useTemplateData } from "../../../../hooks/useTemplateData";
import { AssetWrapperButton } from "./AssetWrapperButton";
import { useMasonryGalleryLayout } from "../hooks/useMasonryGalleryLayout";

type ScrollableMediaGalleryProps = {
  currentAssetLibraryId: string;
  previewWidth: number;
  previewHeight: number;
  assetsFromBrandkitAndLibrary: (Partial<MediaAsset> | BrandKitAsset)[];
  mediaAsset?: MediaAssetTabs;
  scrollableTarget?: string;
  onNext?: () => void;
  hasMore?: boolean;
  isLoading?: boolean;
  onReplaceSelectedAsset?: (
    assetId: string,
    path: string,
    mediaType: MediaKind
  ) => void;
  customAddToCanvas?: (assetId: string, path: string) => void;
  selectionMode?: boolean;
};

const extractAssetIdAndPath = (
  asset: GalleryAsset
): { assetId: string; path: string } | undefined => {
  if (
    "asset_id" in asset &&
    typeof asset.asset_id === "string" &&
    typeof asset.path === "string"
  ) {
    return { assetId: asset.asset_id, path: asset.path };
  }
  if (
    "id" in asset &&
    typeof asset.id === "string" &&
    typeof asset.path === "string"
  ) {
    return { assetId: asset.id, path: asset.path };
  }
  return undefined;
};

export const ScrollableMediaGallery = ({
  currentAssetLibraryId,
  previewWidth,
  previewHeight,
  assetsFromBrandkitAndLibrary,
  mediaAsset = MediaAssetTabs.Images,
  scrollableTarget,
  onNext,
  hasMore,
  isLoading,
  onReplaceSelectedAsset,
  customAddToCanvas,
  selectionMode,
}: ScrollableMediaGalleryProps) => {
  const isImagesTab = mediaAsset === MediaAssetTabs.Images;

  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    mediaAssetsData: { deleteAsset },
  } = useTemplateData();

  const isMediaAsset = (
    item: Partial<MediaAsset> | BrandKitAsset
  ): item is Partial<MediaAsset> & { id: string } => {
    return !("purpose" in item) && typeof item.id === "string";
  };

  const filteredAssets = useMemo(
    () =>
      assetsFromBrandkitAndLibrary.filter(
        (item): item is GalleryAsset => "purpose" in item || isMediaAsset(item)
      ),
    [assetsFromBrandkitAndLibrary]
  );

  const getAspectRatio = useCallback((asset: GalleryAsset): number | null => {
    if ("purpose" in asset) return null;
    if (typeof asset.aspect_ratio === "number" && asset.aspect_ratio > 0) {
      return asset.aspect_ratio;
    }
    if (
      typeof asset.width === "number" &&
      typeof asset.height === "number" &&
      asset.width > 0 &&
      asset.height > 0
    ) {
      return asset.width / asset.height;
    }
    return null;
  }, []);

  const { galleryRef, breakpoints, galleryAssets, groupBy, renderGroup } =
    useMasonryGalleryLayout<GalleryAsset>({
      items: filteredAssets,
      getAspectRatio,
    });

  const fetchNewPageOnScroll = () => {
    if (hasMore) {
      onNext?.();
    }
  };

  const handleMouseEnter = (assetId: string) => {
    // Clear any existing timeout if any
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredItemId(assetId);
  };

  const handleMouseLeave = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const relatedTarget = e.relatedTarget as HTMLElement;

    // Don't hide if moving to popover content or dangerous button
    if (
      target.closest(".ant-popover-content") ||
      target.closest(".ant-btn-dangerous") ||
      relatedTarget.closest(".ant-popover-content") ||
      relatedTarget.closest(".ant-btn-dangerous")
    ) {
      return;
    }

    // Add a small delay before hiding to allow moving to popover
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredItemId(null);
    }, 100);
  };

  return (
    <div ref={galleryRef} style={{ width: "100%" }}>
      <InfiniteScrollList
        assetLength={galleryAssets.length}
        onNext={fetchNewPageOnScroll}
        dataSource={galleryAssets}
        hasMore={hasMore ?? false}
        scrollableTarget={
          scrollableTarget ?? `video-editor-panel-${mediaAsset}`
        }
        loading={isLoading}
        groupBy={groupBy}
        renderGroup={renderGroup}
        breakpoints={breakpoints}
        rowKey={(item) => {
          if (isMediaAsset(item)) {
            return item.id;
          }
          return item.asset_id;
        }}
      >
        {(asset) => {
          const assetId = getRowKey(asset);
          if (!assetId) return null;
          const showOverlay = selectionMode || hoveredItemId === assetId;
          return (
            <Flex
              gap={5}
              vertical
              className="gallery-item"
              style={{ width: "100%" }}
            >
              <AssetWrapperButton
                onMouseEnter={() => handleMouseEnter(assetId)}
                onMouseLeave={handleMouseLeave}
                onClick={
                  onReplaceSelectedAsset
                    ? (e) => {
                        e.stopPropagation();
                        const extracted = extractAssetIdAndPath(asset);
                        if (extracted) {
                          onReplaceSelectedAsset(
                            extracted.assetId,
                            extracted.path,
                            isImagesTab ? "image" : "video"
                          );
                        }
                      }
                    : undefined
                }
                style={{
                  width: "100%",
                  cursor: onReplaceSelectedAsset ? "pointer" : "default",
                }}
              >
                <MediaAssetPreview asset={asset} cardWidth="100%" />
                {showOverlay && (
                  <AssetControlsOverlay
                    item={asset}
                    isVideo={!isImagesTab}
                    previewWidth={previewWidth}
                    previewHeight={previewHeight}
                    customAddToCanvas={customAddToCanvas}
                    selectionMode={selectionMode}
                    deleteButton={
                      isMediaAsset(asset) &&
                      assetId && (
                        <DeleteButton
                          recordItemId={assetId}
                          resource={`media/asset_libraries/${currentAssetLibraryId}/assets`}
                          onSuccess={() => deleteAsset(assetId)}
                        />
                      )
                    }
                  />
                )}
              </AssetWrapperButton>
            </Flex>
          );
        }}
      </InfiniteScrollList>
    </div>
  );
};
